/* A team's games: list, create a new PvAI game, and jump in to play. */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Strategy, BrandStrat } from '@venturewrist/shared';
import { useTeam, useCreateGame } from '../../hooks/queries';
import { ApiError } from '../../lib/api';
import { STRATEGIES, type Named } from '../../lib/constants';
import { Button, Field, Input, Select, Badge, EmptyNote } from '../../components/ui';

const STATUS_TONE: Record<string, 'good' | 'warn' | 'mut'> = {
  active: 'good', setup: 'warn', finished: 'mut',
};
const strategyName = (id: string): string =>
  (STRATEGIES as Named[]).find((s) => s.id === id)?.name ?? id;

export function TeamPanel({ teamId }: { teamId: string }) {
  const team = useTeam(teamId);
  const createGame = useCreateGame();
  const [open, setOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [strategy, setStrategy] = useState<Strategy>('premium');
  const [brandStrat, setBrandStrat] = useState<BrandStrat>('range');

  if (team.isLoading) return <div className="text-sm text-mut">Loading team…</div>;
  const games = team.data?.games ?? [];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    createGame.mutate(
      { teamId, companyName, strategy, brandStrat },
      { onSuccess: () => { setOpen(false); setCompanyName(''); team.refetch(); } },
    );
  };

  return (
    <div className="space-y-3">
      {games.length === 0 && <EmptyNote>No games yet. Start one to begin playing.</EmptyNote>}
      {games.map((g) => (
        <div key={g.id} className="flex items-center justify-between rounded-sm border border-card-b bg-bg2/50 px-3 py-2">
          <div>
            <div className="font-semibold">{g.companyName}</div>
            <div className="text-xs text-mut">
              {strategyName(g.strategy)} · round {g.currentRound}
              {g.composite != null && ` · composite ${Math.round(g.composite)}`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge tone={STATUS_TONE[g.status] ?? 'mut'}>{g.status}</Badge>
            <Link to={`/play/${g.id}`} className="btn-primary px-3 py-1.5">
              {g.status === 'finished' ? 'Review' : 'Play'}
            </Link>
          </div>
        </div>
      ))}

      {open ? (
        <form onSubmit={submit} className="space-y-3 rounded-sm border border-card-b bg-bg2/40 p-3">
          <Field label="Company name">
            <Input required value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g. Lumina Wearables" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Strategy">
              <Select value={strategy} onChange={(e) => setStrategy(e.target.value as Strategy)}>
                {(STRATEGIES as Named[]).map((s) => (
                  <option key={s.id} value={s.id}>{(s.icon as string) ?? ''} {s.name}</option>
                ))}
              </Select>
            </Field>
            <Field label="Brand strategy">
              <Select value={brandStrat} onChange={(e) => setBrandStrat(e.target.value as BrandStrat)}>
                <option value="range">Range brand (shared)</option>
                <option value="unique">Unique brand (per product)</option>
              </Select>
            </Field>
          </div>
          {createGame.error && <div className="text-sm text-bad">{(createGame.error as ApiError).message}</div>}
          <div className="flex gap-2">
            <Button type="submit" disabled={createGame.isPending}>{createGame.isPending ? 'Creating…' : 'Create game'}</Button>
            <Button type="button" variant="ghost" onClick={() => { setOpen(false); createGame.reset(); }}>Cancel</Button>
          </div>
        </form>
      ) : (
        <Button variant="ghost" onClick={() => setOpen(true)}>+ New game</Button>
      )}
    </div>
  );
}
